import React, { useState, useEffect } from 'react';
import { FiPlus, FiCalendar, FiFlag, FiFileText, FiTag, FiUser } from 'react-icons/fi';
import './CreateSubtaskModal.css';

const CreateSubtaskModal = ({ parentTask, users, onClose, onCreate, API_URL }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assignedTo: [],
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        tags: ''
    });
    const [loading, setLoading] = useState(false);

    // Set max due date to parent task's due date
    const maxDueDate = parentTask?.dueDate 
        ? new Date(parentTask.dueDate).toISOString().slice(0, 10) 
        : '';

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            alert('Please enter a subtask title');
            return;
        }

        if (!formData.description.trim()) {
            alert('Please enter a subtask description');
            return;
        }

        if (formData.assignedTo.length === 0) {
            alert('Please select at least one assignee');
            return;
        }

        if (!formData.dueDate) {
            alert('Please select a due date');
            return;
        }

        // Combine date and time
        const timeToUse = formData.dueTime || '23:59';
        const dueDateTimeString = `${formData.dueDate}T${timeToUse}:00`;

        const subtaskData = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            assignedTo: formData.assignedTo,
            priority: formData.priority,
            dueDate: dueDateTimeString,
            tags: formData.tags.trim()
        };

        onCreate(subtaskData);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAssigneeChange = (userId) => {
        setFormData(prev => ({
            ...prev,
            assignedTo: prev.assignedTo.includes(userId)
                ? prev.assignedTo.filter(id => id !== userId)
                : [...prev.assignedTo, userId]
        }));
    };

    if (!parentTask) return null;

    return (
        <div className="create-subtask-modal-overlay" onClick={onClose}>
            <div className="create-subtask-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="create-subtask-modal-header">
                    <div className="create-subtask-modal-title-section">
                        <div className="create-subtask-header-icon">
                            <FiPlus size={24} />
                        </div>
                        <h2>Create Subtask</h2>
                        <button 
                            className="create-subtask-modal-close-btn" 
                            onClick={onClose}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="parent-task-info">
                        Parent Task: <span className="parent-task-badge">{parentTask.taskId}</span> - {parentTask.title}
                    </div>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit}>
                    <div className="create-subtask-modal-body">
                        {/* Title */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiFileText size={18} />
                                <span>Subtask Details</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="subtask-title">
                                    Title <span className="required">*</span>
                                </label>
                                <input
                                    id="subtask-title"
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Enter subtask title"
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="subtask-description">
                                    Description <span className="required">*</span>
                                </label>
                                <textarea
                                    id="subtask-description"
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Enter subtask description"
                                    className="form-control"
                                    rows={4}
                                    required
                                />
                            </div>
                        </div>

                        {/* Assignees */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiUser size={18} />
                                <span>Assign To <span className="required">*</span></span>
                            </div>
                            <div className="assignee-list">
                                {users && users.map(user => (
                                    <label key={user._id} className="assignee-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={formData.assignedTo.includes(user._id)}
                                            onChange={() => handleAssigneeChange(user._id)}
                                        />
                                        <span className="assignee-name">{user.fullName}</span>
                                        <span className="assignee-role">({user.role})</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Priority & Due Date */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiFlag size={18} />
                                <span>Priority & Schedule</span>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="subtask-priority">
                                        Priority <span className="required">*</span>
                                    </label>
                                    <select
                                        id="subtask-priority"
                                        value={formData.priority}
                                        onChange={(e) => handleChange('priority', e.target.value)}
                                        className="form-control"
                                        required
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="subtask-due-date">
                                        Due Date <span className="required">*</span>
                                    </label>
                                    <input
                                        id="subtask-due-date"
                                        type="date"
                                        value={formData.dueDate}
                                        onChange={(e) => handleChange('dueDate', e.target.value)}
                                        max={maxDueDate}
                                        className="form-control"
                                        required
                                    />
                                    <div className="form-hint">
                                        Must be on or before parent task due date: {new Date(parentTask.dueDate).toLocaleDateString('en-GB')}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="subtask-due-time">
                                        Due Time
                                    </label>
                                    <input
                                        id="subtask-due-time"
                                        type="time"
                                        value={formData.dueTime}
                                        onChange={(e) => handleChange('dueTime', e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiTag size={18} />
                                <span>Tags (Optional)</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="subtask-tags">
                                    Tags
                                </label>
                                <input
                                    id="subtask-tags"
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => handleChange('tags', e.target.value)}
                                    placeholder="e.g. urgent, client-request (comma separated)"
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="create-subtask-modal-footer">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            <FiPlus size={18} />
                            {loading ? 'Creating...' : 'Create Subtask'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateSubtaskModal;
